import apiClient from '../server';

// Backoffice API wrapper for the api-gateway clubs-scraper enrichment
// orchestrator. Mirrors the 5 endpoints documented in
// clubs-scraper-backoffice.md on the backend repo.

/**
 * Kick off a one-click full enrichment for a single city.
 * Returns immediately with { jobId, status }.
 *
 * @param {Object} payload
 * @param {string} payload.cityId - MongoId of the City doc
 * @param {boolean} [payload.includeClubs=true]
 * @param {boolean} [payload.includePromoters=true]
 * @param {boolean} [payload.includeArtistsAndEvents=true]
 * @param {boolean} [payload.includeUpcoming=true]
 * @param {boolean} [payload.includePast=false]   - OPT-IN, heavy
 * @param {number}  [payload.maxVenues=500]
 * @param {number}  [payload.maxEventsPerVenue=30]
 * @param {number}  [payload.limit=500]
 * @param {boolean} [payload.polishWithGpt=true]
 * @param {boolean} [payload.scrapeInstagram=true]
 * @param {boolean} [payload.markVerified=true]
 * @param {number}  [payload.minEventsThisYear=1]
 * @param {boolean} [payload.eventFirst=false] - event-first upcoming sweep: pull EVERY upcoming event in the city and create venues on demand (vs only walking known clubs). Affects the upcoming pass only.
 * @param {number}  [payload.upcomingMonths=6] - how many months ahead the event-first sweep looks
 * @param {boolean} [payload.dryRun=false]
 */
export const enrichCity = async (payload) => {
  const response = await apiClient.post(
    '/admin/clubs-scraper/enrich-city',
    payload,
  );
  return response.data;
};

/**
 * Kick off a bulk enrichment across many cities. With no `cityIds` in the
 * body, defaults to every City doc whose name matches an entry in
 * city-to-ra-area.ts (~80 cities).
 *
 * @param {Object} payload
 * @param {string[]} [payload.cityIds]
 * (all other fields same as enrichCity)
 */
export const enrichAllCities = async (payload = {}) => {
  const response = await apiClient.post(
    '/admin/clubs-scraper/enrich-all-cities',
    payload,
  );
  return response.data;
};

/**
 * Fetch a single job's full state (status, cities, steps, totals, errors).
 * @param {string} jobId
 */
export const getEnrichmentJob = async (jobId) => {
  const response = await apiClient.get(
    `/admin/clubs-scraper/jobs/${jobId}`,
  );
  return response.data;
};

/**
 * List recent enrichment jobs. Returns { items, total }.
 * @param {Object} [filters]
 * @param {('pending'|'running'|'completed'|'failed'|'cancelling'|'cancelled'|'interrupted')} [filters.status]
 * @param {('enrich-city'|'enrich-all-cities')} [filters.type]
 * @param {number} [filters.limit=20]
 * @param {number} [filters.skip=0]
 */
export const listEnrichmentJobs = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.type) params.append('type', filters.type);
  if (filters.limit !== undefined) params.append('limit', String(filters.limit));
  if (filters.skip !== undefined) params.append('skip', String(filters.skip));
  const qs = params.toString();
  const response = await apiClient.get(
    `/admin/clubs-scraper/jobs${qs ? `?${qs}` : ''}`,
  );
  return response.data;
};

/**
 * Request cancellation of a running/pending job. The orchestrator will stop
 * at the next clean boundary (between steps or between cities). In-flight
 * RA calls are not interrupted.
 * @param {string} jobId
 */
export const cancelEnrichmentJob = async (jobId) => {
  const response = await apiClient.post(
    `/admin/clubs-scraper/jobs/${jobId}/cancel`,
  );
  return response.data;
};

// Terminal status set (job won't change once it reaches one of these).
// `interrupted` means the api-gateway restarted mid-run (e.g. nest --watch
// recompile, deploy). The orchestrator does NOT auto-resume — the operator
// is expected to re-run the same job; RA-sourced events/profiles dedupe,
// so it's safe and idempotent.
/**
 * Surgical address backfill for the EM (club) profiles already in a city.
 * Pulls each one's address from RA via its stored `scrapSource` and patches
 * `profile.address` when it's missing, looks like a bare label, mentions a
 * foreign country, or has bad/integer-rounded coordinates.
 * CREATES NOTHING. Safe to re-run.
 *
 * @param {Object} payload
 * @param {string} payload.cityId
 * @param {boolean} [payload.onlyMissing=true]
 * @param {boolean} [payload.forceRefresh=false] - override heuristics, re-pull every profile from RA
 */
export const backfillProfileAddresses = async (payload) => {
  const response = await apiClient.post(
    '/admin/clubs-scraper/backfill-addresses',
    payload,
  );
  return response.data;
};

/**
 * Surgical address backfill for events. For each EM profile in the city, copies
 * the profile's (now-correct) address onto every Event whose `creater` is that
 * profile. Same heuristic as the profile backfill (catches missing addresses,
 * bare labels, foreign-country mismatches, and bad coordinates).
 * CREATES NOTHING. Safe to re-run.
 *
 * @param {Object} payload
 * @param {string} payload.cityId
 * @param {boolean} [payload.onlyMissing=true]
 * @param {boolean} [payload.forceRefresh=false]
 */
export const backfillEventAddresses = async (payload) => {
  const response = await apiClient.post(
    '/admin/clubs-scraper/backfill-event-addresses',
    payload,
  );
  return response.data;
};

/**
 * ONE-SHOT fix for a city: heals EM profile addresses first (RA + Mapbox
 * geocode fallback for bad coords), then patches every UPCOMING event whose
 * creater is one of those profiles. Creates nothing. Safe to re-run.
 *
 * @param {Object} payload
 * @param {string} payload.cityId
 * @param {boolean} [payload.forceRefresh=false] - re-pull every profile from RA + overwrite every event
 */
export const fixCityAddresses = async (payload) => {
  const response = await apiClient.post(
    '/admin/clubs-scraper/fix-city-addresses',
    payload,
  );
  return response.data;
};

export const TERMINAL_JOB_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'interrupted',
]);

export const isTerminalStatus = (status) =>
  TERMINAL_JOB_STATUSES.has(status);
